import React from 'react';

import {getLabelColumns} from 'bulk-import/getLabelColumns';
import getRegistryEntityRequiredColumnsError from 'bulk-import/getRegistryEntityRequiredColumnsError';
import ImportFilesTutorialLink from 'bulk-import/ImportFilesTutorialLink';
import {GetRequiredColumnsErrorFunction} from 'bulk-import/panes/ColumnsErrorBanner';
import {configureMapColumnNamesModalPane} from 'bulk-import/panes/MapColumnNamesModalPane';
import ShowErrorsModalPane from 'bulk-import/panes/ShowErrorsModalPane';
import UploadSpreadsheetModalPane from 'bulk-import/panes/UploadSpreadsheetModalPane';
import * as SpreadsheetBulkImportActions from 'bulk-import/SpreadsheetBulkImportActions';
import {
  COLUMN_TYPE,
  DERIVED_COLUMNS_SUPPORTED_XFORM_CONFIGS,
  DerivedColumnsStoreKeys,
  GetValidColumnTypesFunction,
  GetSchemaFieldsToHideByColumnFunction,
  IMPORT_TYPE,
  Pane,
  PaneKey,
  RegistryImportSettingsProps,
  SelectedSchemaIds,
  SPREADSHEET_BULK_IMPORT_KEY,
  SpreadsheetColumn,
  STORABLE_COLUMN_TYPES,
  TaskState,
  TRANSFER_MEASUREMENT_TYPES,
} from 'bulk-import/SpreadsheetBulkImportConstants';
import SpreadsheetBulkImportModalContainer from 'bulk-import/SpreadsheetBulkImportModalContainer';
import * as SpreadsheetBulkImportUtil from 'bulk-import/SpreadsheetBulkImportUtil';
import * as StorableColumnsUtil from 'bulk-import/storables/StorableColumnsUtil';
import {SchemalessBulkImportSupportedOligoAwareTypes} from 'constants/RegistryConstants';
import {NavStoreSource} from 'constants/SourceConstants';
import {UploadFlowType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ComplexPolymerSchemaConfigGraphQL} from 'modbio/complex-polymers/ComplexPolymerUtil';
import {canSchemaHaveMods} from 'molbio/OligoUtil';
import {
  SUBJECT_FIELD_DISPLAY_NAME,
  SUBJECT_LABEL_FIELD_DISPLAY_NAME,
} from 'registry/trial-interfaces/TrialInterfaceConstants';
import * as ModalContainer from 'ui-library/ModalContainer';

export interface BulkCreateTrialSamplesFlowProps {
  initialRegistry: NavStoreSource | null;
  onRequestClose: () => void;
  complexPolymerSchemaConfigurations?: Array<ComplexPolymerSchemaConfigGraphQL>;
}

/**
 * This flow currently only supports schemas implementing the trial sample SSI for trial samples, and schemas
 * implementing the trial subject SSI for trial subjects.
 *
 * The BulkCreateTrialSamplesFlow makes use of:
 * - selectedSchemaIds.tagSchemaId to track the trial sample
 * - selectedSchemaIds.trialSubjectSchemaId to track the trial subject
 * - selectedSchemaIds.containerSchemaId to track the container
 */
export default function BulkCreateTrialSamplesFlow(props: BulkCreateTrialSamplesFlowProps): JSX.Element {
  /**
   * Returns an error message for the first missing required column found.
   *
   * Spreadsheet columns required for trial sample ingestion include:
   * - label columns for trial samples and subjects, whose specific column type is based off their respective
   *   chosen labeling strategies.
   * - trial subject 'Subject label' schema field
   * - study labels
   * - other registry entity required columns
   *
   * @param options.registryImportSettings - Contains the labeling strategy information used for retrieving
   *        label columns necessary for that strategy.
   * @param options.selectedSchemaIds - Includes schema ids for trial sample, trial subject, and container
   *        schema ids, used in determining required columns.
   *
   * @returns An error message if any required columns are missing, null otherwise.
   */
  const getRequiredColumnsError: GetRequiredColumnsErrorFunction = (
    columns: Array<SpreadsheetColumn>,
    options: {
      selectedSchemaIds: SelectedSchemaIds;
      registryImportSettings: RegistryImportSettingsProps;
      schemalessCreationEntityType?: SchemalessBulkImportSupportedOligoAwareTypes;
    }
  ): string | null => {
    const {selectedSchemaIds, registryImportSettings} = options;

    const requiredParentStorableError = StorableColumnsUtil.getRequiredParentStorableError(columns);
    if (requiredParentStorableError) {
      return requiredParentStorableError;
    }

    const registryEntityRequiredColumnsError = getRegistryEntityRequiredColumnsError(columns, {
      selectedSchemaIds,
      registryImportSettings,
      importType: IMPORT_TYPE.TRIAL_SAMPLES,
    });
    if (registryEntityRequiredColumnsError) {
      return registryEntityRequiredColumnsError;
    }

    const requiredFieldError = SpreadsheetBulkImportUtil.getRequiredFieldError(
      [COLUMN_TYPE.STUDY_LABELS],
      columns,
      selectedSchemaIds,
      IMPORT_TYPE.TRIAL_SAMPLES
    );
    if (requiredFieldError) {
      return requiredFieldError;
    }

    const requiredSchemaFieldError = SpreadsheetBulkImportUtil.getRequiredSchemaFieldError(
      columns,
      new Map([[COLUMN_TYPE.TRIAL_SUBJECT_SCHEMA_FIELD, [SUBJECT_LABEL_FIELD_DISPLAY_NAME]]])
    );
    if (requiredSchemaFieldError) {
      return requiredSchemaFieldError;
    }

    return null;
  };

  /**
   * Returns an array of valid column types for the trial samples import flow.
   *
   * These include the label columns for trial samples and subjects, whose specific column type is based off
   * their respective chosen labeling strategies
   *
   * Schema fields for trial sample and trial subjects are also included. Most importantly, this includes the
   * trial subject 'Subject label' schema field, which is required for ingestion.
   *
   * Other column types for study labels, trial samples, and containers are also included.
   */
  const getValidColumnTypes: GetValidColumnTypesFunction = ({
    registryImportSettings,
    selectedSchemaIds,
  }: {
    registryImportSettings: RegistryImportSettingsProps;
    selectedSchemaIds: SelectedSchemaIds;
  }): Array<COLUMN_TYPE> => {
    const labelColumns = getLabelColumns(registryImportSettings);
    const containerColumnTypes = [
      ...STORABLE_COLUMN_TYPES,
      COLUMN_TYPE.CONTAINER_SCHEMA_FIELD,
      ...TRANSFER_MEASUREMENT_TYPES,
      ...StorableColumnsUtil.getSampleControlRolesColumns(),
    ];

    return [
      ...labelColumns,
      COLUMN_TYPE.STUDY_LABELS,
      ...SpreadsheetBulkImportUtil.getPolymerColumnTypes(
        SpreadsheetBulkImportUtil.shouldHaveResidues(selectedSchemaIds),
        canSchemaHaveMods(SpreadsheetBulkImportUtil.getTagSchema(selectedSchemaIds)!)
      ),
      ...SpreadsheetBulkImportUtil.getMoleculeColumnTypes(
        SpreadsheetBulkImportUtil.shouldHaveChemicalStructure(selectedSchemaIds)
      ),
      ...SpreadsheetBulkImportUtil.getComplexPolymerColumnTypes(
        selectedSchemaIds,
        props.complexPolymerSchemaConfigurations
      ),
      // Don't include `...VALUE_COLUMN_TYPES` here because we don't want alias, custom field, or description,
      // since we'd need to support those also for subject, which would take some work and is not needed
      COLUMN_TYPE.SCHEMA_FIELD,
      COLUMN_TYPE.IGNORE,
      COLUMN_TYPE.TRIAL_SUBJECT_SCHEMA_FIELD,
      ...containerColumnTypes,
    ];
  };

  const getSchemaFieldsToHideByColumnType: GetSchemaFieldsToHideByColumnFunction = (): Map<
    COLUMN_TYPE,
    Array<string>
  > => new Map([[COLUMN_TYPE.SCHEMA_FIELD, [SUBJECT_FIELD_DISPLAY_NAME]]]);

  function getPanes(): Array<Pane> {
    return [
      {
        paneKey: PaneKey.SET_TRIAL_SAMPLES_IMPORT_SETTINGS,
        configuration: null,
      },
      {
        paneKey: PaneKey.UPLOAD_SPREADSHEET,
        configuration: UploadSpreadsheetModalPane.configure({
          getExampleImageURL({selectedSchemaIds}: {selectedSchemaIds: SelectedSchemaIds}): string {
            // Show screenshots with 'Bases' if importing sequences ('Amino Acids', proteins).
            const tagSchema = SpreadsheetBulkImportUtil.getTagSchema(selectedSchemaIds);
            return SpreadsheetBulkImportUtil.getExampleImageURLForTagSchema(tagSchema);
          },
        }),
      },
      {
        paneKey: PaneKey.MAP_COLUMN_NAMES,
        configuration: configureMapColumnNamesModalPane({
          getValidColumnTypes,
          getSchemaFieldsToHideByColumnType,
          getRequiredColumnsError,
          onValidateSpreadsheet: SpreadsheetBulkImportActions.validateSpreadsheetForTrialSamples,
          customHandleGoToNextPaneFn: (paneProps) => {
            if (SpreadsheetBulkImportUtil.shouldCheckDerivedColumns(paneProps.fileData.columns)) {
              SpreadsheetBulkImportActions.findDerivedColumns();
              SpreadsheetBulkImportActions.goToNextPane();
            } else {
              paneProps.onValidateSpreadsheet();
              SpreadsheetBulkImportActions.goToPaneWithKey(PaneKey.SHOW_ERRORS);
            }
          },
          savedMappingsConfig: {
            enableSavedMappings: true,
            uploadFlow: UploadFlowType.IMPORT_TRIAL_SAMPLES,
          },
        }),
      },
      {
        paneKey: PaneKey.MAP_DERIVED_COLUMNS,
        configuration: {
          columnTransformConfigs: DERIVED_COLUMNS_SUPPORTED_XFORM_CONFIGS,
          derivedColumnsKey: DerivedColumnsStoreKeys.BULK_CREATE,
          onUpdateDerivedColumns: SpreadsheetBulkImportActions.setDerivedColumns,
          onValidateSpreadsheet: SpreadsheetBulkImportActions.validateSpreadsheetForTrialSamples,
        },
      },
      {
        paneKey: PaneKey.SHOW_ERRORS,
        configuration: ShowErrorsModalPane.configure({
          customHandleGoToPrevPaneFn: (paneProps) => {
            if (
              paneProps.derivedColumnsData.state === TaskState.COMPLETED &&
              paneProps.derivedColumnsData.derivedColumns &&
              paneProps.derivedColumnsData.derivedColumns.length > 0
            ) {
              SpreadsheetBulkImportActions.goToPrevPane();
            } else {
              SpreadsheetBulkImportActions.goToPaneWithKey(PaneKey.MAP_COLUMN_NAMES);
            }
          },
          enableReplaceLinkWithText: true,
          enableSkipErrors: true,
          uploadFlow: UploadFlowType.IMPORT_TRIAL_SAMPLES,
        }),
      },
    ];
  }

  return (
    <SpreadsheetBulkImportModalContainer
      footer={<ImportFilesTutorialLink />}
      importType={IMPORT_TYPE.TRIAL_SAMPLES}
      initialSettings={{initialRegistry: props.initialRegistry}}
      onImportFromSpreadsheet={SpreadsheetBulkImportActions.createTrialSamplesFromSpreadsheet}
      onRequestClose={props.onRequestClose}
      panes={getPanes()}
      title="Import trial samples from spreadsheet"
      complexPolymerSchemaConfigurations={props.complexPolymerSchemaConfigurations}
    />
  );
}

export function openBulkCreateTrialSamplesFlow(
  props: Omit<BulkCreateTrialSamplesFlowProps, 'onRequestClose'>
): void {
  ModalContainer.open(
    SPREADSHEET_BULK_IMPORT_KEY,
    <BulkCreateTrialSamplesFlow {...props} onRequestClose={SpreadsheetBulkImportActions.closeModal} />
  );
}
